import { ArrowLeftIcon, LogoFooterSetUpIcon } from '@/components/icons'
import { OPERATIONS_ROUTES } from '@/constant/route'
import useUser from '@/features/auth/hooks/useUser'
import {
  useCommonData,
  useOverAllStatus,
} from '@/features/design-system/hooks/setup'
import { updateInterfaceStatus } from '@/store/slices/interfaceByOrganization/interfaceByOrganizationSlice'
import { useAppDispatch } from '@/store/stores'
import { User } from '@/types/user'
import useSearchInterface from '@features/admin/hooks/interface/useSearchInterface'
import useValidateCurrentInterface from '@features/design-system/hooks/setup/useValidateCurrentInterface'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import SetUpHeader from '../../../common/SetUpHeader'
import SetUpLayout from '../../../common/SetUpLayout'
import BoxWrapper from '../BoxWrapper'

type Props = {}

const SetUpFirst = (props: Props) => {
  // SCRUM-976: pin the setup step to the URL :id (single source of truth), not the global
  // selected interface (AuthContext/localStorage), which a second tab or a stale refresh can change.
  const { id } = useParams()
  const interfaceId = Number(id)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { data: user } = useUser<User>()
  const { data: status } = useOverAllStatus(interfaceId)
  const { data: commonDataStatus } = useCommonData(interfaceId)

  const { data: allInterfaces } = useSearchInterface()

  const { isValidInterface, selectedInterface, isLoading } =
    useValidateCurrentInterface({
      currentInterfaceId: id,
      allInterfaces,
      redirectTo: OPERATIONS_ROUTES.index.to,
      /**
       * SCRUM-1032 QC item 16 — a controller that is already set up does not
       * belong on Getting Started.
       *
       * This page had its own redirect, but on `useOverAllStatus().percentage
       * === 100` — a computed measure of how much of the setup has been done,
       * not whether the controller is marked set up. The two disagree: D1 is
       * set up and its percentage reads 0, so the page sat there offering to
       * start a setup that had already finished, with no way past it.
       *
       * The hook redirects on the interface's own `status`, which is the flag
       * the route guards read, and syncs Redux on the way out so the dashboard
       * does not bounce the user straight back. The percentage effect below
       * stays: it is what carries the user out at the moment they finish.
       */
      autoRedirect: true,
    })

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined

    if (status && status.percentage === 100) {
      // Set a new timeout to navigate after 1 second
      timeoutId = setTimeout(() => {
        // Sync Redux store before navigating to prevent infinite redirect loop:
        // SelectLabelDashboard on the dashboard reads status from Redux (stale false)
        // and would bounce the user straight back here. Same pattern as SetUpSecondV2.
        if (!Number.isNaN(interfaceId)) {
          dispatch(updateInterfaceStatus({ id: interfaceId, status: true }))
        }
        navigate(OPERATIONS_ROUTES.index.to)
      }, 1000)
    }

    // Cleanup the timeout on component unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [status, navigate, dispatch, interfaceId])

  // useEffect(() => {
  //   if (!user) return
  //   if (!setupHelper.isDoneAccountConfig(user)) {
  //     navigate(CONFIG_SETUP_ROUTES.index.to)
  //   }
  // }, [user, navigate])

  return (
    <SetUpLayout
      title="Getting Started"
      header={<SetUpHeader setupFirst={true} />}
      footer={
        <Stack sx={{ color: '#D7D7D7', alignItems: 'center', mb: '20px' }}>
          <LogoFooterSetUpIcon />
        </Stack>
      }
    >
      {status?.percentage === 100 ? (
        <Stack
          flexGrow="1"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      ) : (
        <>
          {(user?.role === 'SuperAdmin' || user?.role === 'Admin') && (
            <Stack
              direction="row"
              spacing="10px"
              component={Link}
              to={`/admin/interface/`}
              sx={{
                textDecoration: 'none',
                width: '150px',
              }}
            >
              <Button
                variant="secondary"
                sx={{
                  padding: '8px',
                  height: '32px',
                  minWidth: '32px',
                }}
              >
                <ArrowLeftIcon />
              </Button>

              <Stack justifyContent="center">
                <Typography
                  sx={{
                    lineHeight: '115%',
                    fontSize: '12px',
                    fontWeight: '400',
                    color: 'text.main',
                  }}
                >
                  Back to Interface
                </Typography>
              </Stack>
            </Stack>
          )}

          <Stack
            flexGrow="1"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'relative',
              // The glow below is a fixed 900px wide, so on a narrow window (or
              // at a high browser zoom) it runs past the right edge and gives
              // the page a horizontal scrollbar over nothing. It is decoration:
              // clip it rather than let it size the page.
              overflowX: 'hidden',
              // A single wide brand glow behind the cards: enough to lift them
              // off the page without the layered blobs reading as noise.
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '12%',
                left: '50%',
                width: 900,
                height: 460,
                transform: 'translateX(-50%)',
                borderRadius: '50%',
                pointerEvents: 'none',
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'radial-gradient(ellipse at center, rgba(10,107,98,0.18) 0%, transparent 68%)'
                    : 'radial-gradient(ellipse at center, rgba(0,71,68,0.055) 0%, transparent 68%)',
              },
            }}
          >
            <Stack
              sx={{
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  color: 'text.main',
                  fontSize: '30px',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                  textAlign: 'center',
                }}
              >
                Getting started
              </Typography>
              <Typography
                sx={{
                  color: 'grey.600',
                  fontSize: '14px',
                  lineHeight: 1.55,
                  textAlign: 'center',
                  maxWidth: 620,
                }}
              >
                Hi {user?.name}, welcome to Cisgenics. Two steps and this site is
                ready to irrigate.
              </Typography>

              <BoxWrapper
                status={status?.percentage ?? 0}
                commonDataStatus={commonDataStatus?.percentage}
              />
            </Stack>
          </Stack>
        </>
      )}
    </SetUpLayout>
  )
}

export default SetUpFirst
